<template>
  <div v-if="isVisible" class="rejection-modal-overlay" @click="handleOverlayClick">
    <div class="rejection-modal-content" @click.stop>
      <!-- Header -->
      <div class="modal-header">
        <div class="modal-icon">❌</div>
        <h3 class="modal-title">Reject Proposal</h3>
      </div>

      <!-- Content -->
      <div class="modal-body">
        <p class="modal-message">Please provide a reason for rejecting this schedule proposal.</p>
        
        <div class="form-group">
          <label for="rejection-reason">Rejection Reason <span class="required">*</span></label>
          <textarea
            id="rejection-reason"
            v-model="rejectionReason"
            class="rejection-textarea"
            rows="5"
            placeholder="Explain why this proposal is being rejected..."
            @keydown.enter.ctrl="handleConfirm"
          ></textarea>
          <small class="help-text">Press Ctrl+Enter to submit</small>
        </div>
      </div>

      <!-- Actions -->
      <div class="modal-actions">
        <button 
          @click="handleCancel"
          class="modal-btn cancel-btn"
        >
          Cancel
        </button>
        <button 
          @click="handleConfirm"
          class="modal-btn reject-btn"
          :disabled="!rejectionReason.trim()"
        >
          Reject Proposal
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

const emit = defineEmits(['confirm', 'cancel'])

const isVisible = ref(false)
const rejectionReason = ref('')

function show() {
  isVisible.value = true
  rejectionReason.value = ''
  // Focus textarea after modal opens
  setTimeout(() => {
    const textarea = document.getElementById('rejection-reason')
    if (textarea) textarea.focus()
  }, 100)
}

function hide() {
  isVisible.value = false
  rejectionReason.value = ''
}

function handleConfirm() {
  if (!rejectionReason.value.trim()) return
  
  emit('confirm', rejectionReason.value)
  hide()
}

function handleCancel() {
  emit('cancel')
  hide()
}

function handleOverlayClick() {
  handleCancel()
}

// Watch for Escape key
watch(isVisible, (visible) => {
  if (visible) {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleCancel()
      }
    }
    document.addEventListener('keydown', handleEscape)
    
    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }
})

// Expose methods for parent component
defineExpose({
  show,
  hide
})
</script>

<style scoped>
.rejection-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.rejection-modal-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow: auto;
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 1.5rem 0 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 1rem;
}

.modal-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.modal-title {
  margin: 0;
  color: #1e293b;
  font-size: 1.25rem;
  font-weight: 600;
}

.modal-body {
  padding: 1.5rem;
}

.modal-message {
  color: #475569;
  line-height: 1.6;
  margin: 0 0 1.5rem 0;
  font-size: 0.95rem;
}

.form-group {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #334155;
  font-weight: 600;
  font-size: 0.9rem;
}

.required {
  color: #ef4444;
}

.rejection-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  transition: all 0.2s ease;
}

.rejection-textarea:focus {
  outline: none;
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.rejection-textarea::placeholder {
  color: #94a3b8;
}

.help-text {
  display: block;
  margin-top: 0.5rem;
  color: #64748b;
  font-size: 0.8rem;
  font-style: italic;
}

.modal-actions {
  display: flex;
  gap: 0.75rem;
  padding: 0 1.5rem 1.5rem 1.5rem;
  justify-content: flex-end;
  border-top: 1px solid #f1f5f9;
  padding-top: 1rem;
}

.modal-btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;
  font-size: 0.9rem;
}

.cancel-btn {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.cancel-btn:hover {
  background: #e2e8f0;
}

.reject-btn {
  background: #ef4444;
  color: white;
}

.reject-btn:hover:not(:disabled) {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.reject-btn:disabled {
  background: #fca5a5;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Mobile responsive */
@media (max-width: 480px) {
  .rejection-modal-content {
    width: 95%;
    margin: 1rem;
  }
  
  .modal-actions {
    flex-direction: column-reverse;
  }
  
  .modal-btn {
    width: 100%;
  }
}
</style>
